/**
 * App Version Delete Dialog Component
 *
 * Confirmation dialog for soft deleting an app version.
 * Sets isActive=false on the version.
 */

import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useDeleteAppVersion } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';
import type { AppVersion } from '@/types/app-version';

interface AppVersionDeleteDialogProps {
  version: AppVersion | null;
  onClose: () => void;
}

/**
 * App Version Delete Dialog
 * Renders confirmation dialog for soft deleting an app version
 */
export function AppVersionDeleteDialog({ version, onClose }: AppVersionDeleteDialogProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteAppVersionMutation = useDeleteAppVersion();

  const handleDelete = async () => {
    if (!version) return;

    setIsDeleting(true);
    try {
      await deleteAppVersionMutation.mutateAsync(version.id);

      toast({
        title: 'Version Deleted',
        description: `Version ${version.version} has been deactivated`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete version',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={version !== null} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete App Version
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete version {version?.version}? This will deactivate the
            version and prevent it from being used for update checks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-md space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Version:</span>
              <span className="font-mono font-medium">{version?.version}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Platform:</span>
              <span className="font-medium">{version?.platform.toUpperCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Bundle ID:</span>
              <span className="font-mono text-xs">{version?.appBundleId}</span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Note: This action will set the version&apos;s status to inactive. The version record
            will remain in the database for historical purposes.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Version
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
